// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCopy, 
    faCode, 
    faCheckCircle,
    faExclamationTriangle,
    faGlobe,
    faShoppingCart,
    faAtom,
    faFileCode,
    faTag
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getClientProfile } from '../services/api';

const IntegrationsEnhanced: React.FC = ({clientId: propClientId}) => {
    const [clientId, setClientId] = useState(propClientId || '');
    const [selectedPlatform, setSelectedPlatform] = useState('');
    // const [showAdvanced, setShowAdvanced] = useState(false);
    const [clientInfo, setClientInfo] = useState({
        name: '',
        website_url: ''
    });
    
    // Get client ID from localStorage if not provided as prop
    useEffect(() => {
        if (!propClientId) {
            const storedClientId = localStorage.getItem('client_id');
            if (storedClientId) {
                setClientId(storedClientId);
            }
        }
    }, [propClientId]);
    
    // Fetch client information
    useEffect(() => {
        const fetchClientInfo = async () => {
            if (clientId) {
                try {
                    const profile = await getClientProfile(clientId);
                    setClientInfo({
                        name: profile.name || 'Unknown',
                        website_url: profile.website_url || 'Not provided'
                    });
                } catch (error) {
                    console.error('Failed to fetch client info:', error);
                }
            }
        };
        
        fetchClientInfo();
    }, [clientId]);
    
    // Get current domain dynamically
    const currentDomain = window.location.origin;
    
    // Basic widget code
    const basicWidgetCode = `<!-- AI Chat Widget -->
<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`;

    // Platform-specific codes with extended CMS support
    const platformCodes = {
        // Popular CMS Platforms
        wordpress: {
            name: 'WordPress',
            icon: faGlobe,
            category: 'CMS',
            difficulty: 'Easy with Plugin',
            timeEstimate: '5 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '🟢 EASIEST METHOD - Use a Plugin:',
                '1. In WordPress Admin, go to "Plugins" → "Add New"',
                '2. Search for "Insert Headers and Footers"',
                '3. Install and activate the plugin by WPBeginner',
                '4. Go to "Settings" → "Insert Headers and Footers"',
                '5. Paste the code above in the "Scripts in Footer" box',
                '6. Click the blue "Save" button',
                '7. Done! The widget will appear on all pages',
                '',
                '⚠️ If the plugin method doesn\'t work, ask the website developer to add the code manually'
            ]
        },
        wix: {
            name: 'Wix',
            icon: faGlobe,
            category: 'Website Builder',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Open your site in the Wix Editor',
                'Click the "+" button on the left sidebar',
                'Select "Embed" → "HTML iframe"',
                'Click "Enter Code"',
                'Select "Code" (not "Website Address")',
                'Paste the widget code',
                'Click "Apply"',
                'Drag the element off-screen (it will still work)',
                'Publish your site'
            ]
        },
        squarespace: {
            name: 'Squarespace',
            icon: faGlobe,
            category: 'Website Builder',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Go to Settings → Advanced → Code Injection',
                'Paste the code in the "Footer" section',
                'Click "Save"',
                'The widget will appear on all pages',
                '',
                'For specific pages only:',
                '• Edit the specific page',
                '• Click the gear icon (Page Settings)',
                '• Go to "Advanced"',
                '• Add code to "Page Header Code Injection"'
            ]
        },
        shopify: {
            name: 'Shopify',
            icon: faShoppingCart,
            category: 'E-commerce',
            code: `<!-- Add to theme.liquid -->
{% comment %} AI Chat Widget {% endcomment %}
<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'From your Shopify admin, go to "Online Store"',
                'Click "Themes"',
                'On your current theme, click "Actions" → "Edit code"',
                'In the Layout folder, click "theme.liquid"',
                'Find the </body> tag (usually near the bottom)',
                'Paste the code right before </body>',
                'Click "Save"',
                'The widget will appear on all pages'
            ]
        },
        webflow: {
            name: 'Webflow',
            icon: faGlobe,
            category: 'Website Builder',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Open your project in Webflow',
                'Go to Project Settings (gear icon)',
                'Click on "Custom Code" tab',
                'In the "Footer Code" section, paste the widget code',
                'Click "Save Changes"',
                'Publish your site for changes to take effect',
                '',
                'Note: Custom code only works on paid Webflow plans'
            ]
        },
        weebly: {
            name: 'Weebly',
            icon: faGlobe,
            category: 'Website Builder',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'In the Weebly editor, go to "Settings"',
                'Click on "SEO"',
                'Scroll down to "Footer Code"',
                'Paste the widget code',
                'Click "Save"',
                'Publish your site'
            ]
        },
        joomla: {
            name: 'Joomla',
            icon: faGlobe,
            category: 'CMS',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Go to Extensions → Templates → Templates',
                'Click on your template name',
                'Click "Edit main page template"',
                'Find the </body> tag',
                'Add the widget code before </body>',
                'Click "Save"',
                '',
                'Alternative: Use a Custom HTML module',
                '• Go to Extensions → Modules',
                '• Create new "Custom HTML" module',
                '• Paste the code and assign to all pages'
            ]
        },
        drupal: {
            name: 'Drupal',
            icon: faGlobe,
            category: 'CMS',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Method 1: Using Block System',
                '• Go to Structure → Block layout',
                '• Click "Place block" in the Footer region',
                '• Add a "Custom block"',
                '• Set Text format to "Full HTML"',
                '• Paste the widget code',
                '• Save the block',
                '',
                'Method 2: Theme Template',
                '• Edit your theme\'s html.html.twig file',
                '• Add code before closing </body> tag'
            ]
        },
        hubspot: {
            name: 'HubSpot CMS',
            icon: faGlobe,
            category: 'CMS',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Go to Settings → Website → Pages',
                'Click on "Site header HTML"',
                'Paste the widget code',
                'Click "Save"',
                '',
                'For specific pages:',
                '• Edit the page',
                '• Go to Settings tab',
                '• Advanced options → Head HTML',
                '• Add the code'
            ]
        },
        bigcommerce: {
            name: 'BigCommerce',
            icon: faShoppingCart,
            category: 'E-commerce',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Go to Storefront → Script Manager',
                'Click "Create a Script"',
                'Enter a name like "AI Chat Widget"',
                'Select "Footer" for location',
                'Select "All pages" for placement',
                'Set Script type to "Script"',
                'Paste the widget code',
                'Click "Save"'
            ]
        },
        godaddy: {
            name: 'GoDaddy Website Builder',
            icon: faGlobe,
            category: 'Website Builder',
            difficulty: 'Easy',
            timeEstimate: '3 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. Log in to your GoDaddy account',
                '2. Go to "My Products" and find your website',
                '3. Click "Manage" → "Edit Website"',
                '4. In the website builder, click "Settings" (gear icon)',
                '5. Select "Advanced" → "Custom Code"',
                '6. Paste the code in the "Footer" section',
                '7. Click "Save" and "Publish"',
                '',
                '✅ Very easy - no technical knowledge needed!'
            ]
        },
        elementor: {
            name: 'Elementor (WordPress)',
            icon: faGlobe,
            category: 'Website Builder',
            difficulty: 'Easy',
            timeEstimate: '3 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. In WordPress, edit any page with Elementor',
                '2. Click the hamburger menu (☰) in the top left',
                '3. Go to "Site Settings" → "Custom Code"',
                '4. Click "Add Custom Code"',
                '5. Give it a name like "AI Chat Widget"',
                '6. Paste the code in the text area',
                '7. Set Location to "Body - End"',
                '8. Click "Save & Close"',
                '9. Update/Publish your changes',
                '',
                '✅ Works on all pages automatically!'
            ]
        },
        divi: {
            name: 'Divi (WordPress)',
            icon: faGlobe,
            category: 'Website Builder',
            difficulty: 'Easy',
            timeEstimate: '3 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. In WordPress, go to "Divi" → "Theme Options"',
                '2. Click on the "Integration" tab',
                '3. Find the box labeled "Add code to the < body >"',
                '4. Paste the widget code in this box',
                '5. Click "Save Changes" at the bottom',
                '',
                '✅ That\'s it! The widget will appear on all pages'
            ]
        },
        woocommerce: {
            name: 'WooCommerce (WordPress)',
            icon: faShoppingCart,
            category: 'E-commerce',
            difficulty: 'Easy',
            timeEstimate: '5 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '📌 Same as WordPress - WooCommerce is a WordPress plugin',
                '',
                '1. In WordPress Admin, go to "Plugins" → "Add New"',
                '2. Search for "Insert Headers and Footers"',
                '3. Install and activate the plugin',
                '4. Go to "Settings" → "Insert Headers and Footers"',
                '5. Paste the code in the "Scripts in Footer" box',
                '6. Click "Save"',
                '',
                '💡 The chat widget will help answer product questions!'
            ]
        },
        ghost: {
            name: 'Ghost',
            icon: faGlobe,
            category: 'CMS',
            difficulty: 'Easy',
            timeEstimate: '2 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. Go to your Ghost Admin panel',
                '2. Navigate to "Settings" → "Code injection"',
                '3. Paste the code in the "Site Footer" box',
                '4. Click "Save" in the top right',
                '',
                '✅ Super simple! Works on all pages'
            ]
        },
        strikingly: {
            name: 'Strikingly',
            icon: faGlobe,
            category: 'Website Builder',
            difficulty: 'Easy',
            timeEstimate: '3 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. In your Strikingly editor, go to "Settings"',
                '2. Click on "Show Advanced Options"',
                '3. Find "Footer Code" section',
                '4. Paste the widget code',
                '5. Click "Save"',
                '6. Publish your site',
                '',
                '⚠️ Note: Custom code requires a paid Strikingly plan'
            ]
        },
        jimdo: {
            name: 'Jimdo',
            icon: faGlobe,
            category: 'Website Builder',
            difficulty: 'Medium',
            timeEstimate: '5 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. Log in to your Jimdo account',
                '2. Go to "Settings" → "Edit Head"',
                '3. Paste the code in the text area',
                '4. Save your changes',
                '',
                '⚠️ Note: "Edit Head" feature requires Jimdo Pro or higher',
                '',
                'Alternative for free plans:',
                '• Use the HTML element in page editor',
                '• Add to each page individually'
            ]
        },
        site123: {
            name: 'SITE123',
            icon: faGlobe,
            category: 'Website Builder',
            difficulty: 'Easy',
            timeEstimate: '3 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. In your SITE123 dashboard, go to "Settings"',
                '2. Click on "SEO & Analytics"',
                '3. Select "Custom Code" tab',
                '4. Paste the code in "Body Code" section',
                '5. Click "Save"',
                '',
                '✅ The widget will appear on all pages!'
            ]
        },
        magento: {
            name: 'Magento/Adobe Commerce',
            icon: faShoppingCart,
            category: 'E-commerce',
            difficulty: 'Hard',
            timeEstimate: '15-30 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '⚠️ REQUIRES DEVELOPER ASSISTANCE',
                '',
                'For developers:',
                '1. Access Magento Admin Panel',
                '2. Go to Content → Design → Configuration',
                '3. Edit your store view',
                '4. Under "HTML Head" → "Scripts and Style Sheets"',
                '5. Add the widget code',
                '6. Save Configuration',
                '7. Clear cache',
                '',
                '📧 Send these instructions to your developer'
            ]
        },
        prestashop: {
            name: 'PrestaShop',
            icon: faShoppingCart,
            category: 'E-commerce',
            difficulty: 'Medium',
            timeEstimate: '10 minutes',
            code: `<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                '1. Go to PrestaShop Admin',
                '2. Navigate to "Design" → "Theme & Logo"',
                '3. Click "Advanced Configuration"',
                '4. Find "Custom Code" section',
                '5. Paste the code in "Add extra JavaScript to your pages"',
                '6. Click "Save"',
                '',
                'Alternative method:',
                '• Use a Custom HTML module',
                '• Position it in the footer hook'
            ]
        },
        // Development Frameworks
        html: {
            name: 'HTML/Static Website',
            icon: faFileCode,
            category: 'Development',
            code: `<!-- Add this before </body> tag -->
<script src="${currentDomain}/widget/widget-loader.js" data-client-id="${clientId}"></script>`,
            instructions: [
                'Open your website\'s HTML file(s)',
                'Find the closing </body> tag',
                'Paste the code just before </body>',
                'Save the file(s)',
                'Upload to your web server',
                '',
                'For multiple pages:',
                '• Add to each HTML file, OR',
                '• Add to a common footer include file'
            ]
        },
        react: {
            name: 'React',
            icon: faAtom,
            category: 'Development',
            code: `// Add to your App.js or main component
import { useEffect } from 'react';

function App() {
    useEffect(() => {
        const script = document.createElement('script');
        script.src = '${currentDomain}/widget/widget-loader.js';
        script.setAttribute('data-client-id', '${clientId}');
        script.async = true;
        document.body.appendChild(script);
        
        return () => {
            document.body.removeChild(script);
        };
    }, []);
    
    return (
        // Your app content
    );
}`,
            instructions: [
                'Open your main App component (App.js)',
                'Import useEffect from React',
                'Add the useEffect hook inside your component',
                'The widget will load when the component mounts',
                'It will be removed when the component unmounts',
                '',
                'For Next.js:',
                '• Add to _app.js for all pages',
                '• Or add to specific page components'
            ]
        },
        vue: {
            name: 'Vue.js',
            icon: faAtom,
            category: 'Development',
            code: `// Add to your main component or App.vue
export default {
    mounted() {
        const script = document.createElement('script');
        script.src = '${currentDomain}/widget/widget-loader.js';
        script.setAttribute('data-client-id', '${clientId}');
        script.async = true;
        document.body.appendChild(script);
    },
    beforeDestroy() {
        // Remove script if needed
        const script = document.querySelector(
            'script[src*="widget-loader.js"]'
        );
        if (script) {
            document.body.removeChild(script);
        }
    }
}`,
            instructions: [
                'Open your main Vue component',
                'Add the code to the mounted() lifecycle hook',
                'The widget will load when the component is mounted',
                '',
                'For Nuxt.js:',
                '• Create a plugin file in plugins/',
                '• Add the script in the plugin',
                '• Register in nuxt.config.js'
            ]
        },
        angular: {
            name: 'Angular',
            icon: faAtom,
            category: 'Development',
            code: `// Add to your app.component.ts
import { Component, OnInit } from '@angular/core';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
    ngOnInit() {
        const script = document.createElement('script');
        script.src = '${currentDomain}/widget/widget-loader.js';
        script.setAttribute('data-client-id', '${clientId}');
        script.async = true;
        document.body.appendChild(script);
    }
}`,
            instructions: [
                'Open your app.component.ts file',
                'Import OnInit from @angular/core',
                'Implement OnInit interface',
                'Add the script loading code to ngOnInit()',
                'The widget will load when the app initializes',
                '',
                'Alternative: Add to index.html',
                '• Simply add the HTML version before </body>'
            ]
        },
        // Other Integration Methods
        gtm: {
            name: 'Google Tag Manager',
            icon: faTag,
            category: 'Tag Manager',
            code: `<script>
(function() {
    var script = document.createElement('script');
    script.src = '${currentDomain}/widget/widget-loader.js';
    script.setAttribute('data-client-id', '${clientId}');
    script.async = true;
    document.body.appendChild(script);
})();
</script>`,
            instructions: [
                'Log in to Google Tag Manager',
                'Click "New Tag"',
                'Click "Tag Configuration"',
                'Choose "Custom HTML"',
                'Paste the widget code',
                'Click "Triggering"',
                'Choose "All Pages" (or create custom trigger)',
                'Name your tag (e.g., "AI Chat Widget")',
                'Save and publish your container'
            ]
        }
    };

    // Advanced configuration options
    // const advancedConfig = `<script>
    // // Advanced configuration
    // window.aiChatConfig = {
    //     clientId: '${clientId}',
    //     position: 'bottom-right', // or 'bottom-left'
    //     theme: 'light', // or 'dark'
    //     language: 'en',
    //     initialMessage: 'Hello! How can I help you today?',
    //     hideOnMobile: false,
    //     customStyles: {
    //         primaryColor: '#0066CC',
    //         fontFamily: 'Arial, sans-serif'
    //     }
    // };
    // </script>
    // <script src="${currentDomain}/widget/widget-loader.js"></script>`;

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    // const testWidget = () => {
    //     // Open test page in new window
    //     window.open(`${currentDomain}/widget-test.html?client_id=${clientId}`, '_blank');
    // };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-8">Integration Guide</h1>

            {/* Quick Start Section */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-green-500" />
                    Quick Start
                </h2>
                <div>
                    <h3 className="font-medium mb-2">Copy the Widget Code</h3>
                    <div className="relative">
                        <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
                            <code>{basicWidgetCode}</code>
                        </pre>
                        <button
                            onClick={() => copyToClipboard(basicWidgetCode)}
                            className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon icon={faCopy} />
                        </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-3">
                        Add this code to your website following the platform-specific instructions below.
                    </p>
                </div>
            </div>

            {/* Platform-Specific Instructions */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4">
                    <FontAwesomeIcon icon={faCode} className="mr-2" />
                    Integration Instructions
                </h2>
                
                {/* Platform Selector */}
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">
                        Select your website platform or CMS:
                    </label>
                    <select
                        value={selectedPlatform}
                        onChange={(e) => setSelectedPlatform(e.target.value)}
                        className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">-- Select Platform --</option>
                        
                        {/* Group by category */}
                        <optgroup label="Content Management Systems">
                            {Object.entries(platformCodes)
                                .filter(([_, p]) => p.category === 'CMS')
                                .map(([key, platform]) => (
                                    <option key={key} value={key}>{platform.name}</option>
                                ))}
                        </optgroup>
                        
                        <optgroup label="Website Builders">
                            {Object.entries(platformCodes)
                                .filter(([_, p]) => p.category === 'Website Builder')
                                .map(([key, platform]) => (
                                    <option key={key} value={key}>{platform.name}</option>
                                ))}
                        </optgroup>
                        
                        <optgroup label="E-commerce Platforms">
                            {Object.entries(platformCodes)
                                .filter(([_, p]) => p.category === 'E-commerce')
                                .map(([key, platform]) => (
                                    <option key={key} value={key}>{platform.name}</option>
                                ))}
                        </optgroup>
                        
                        <optgroup label="Development Frameworks">
                            {Object.entries(platformCodes)
                                .filter(([_, p]) => p.category === 'Development')
                                .map(([key, platform]) => (
                                    <option key={key} value={key}>{platform.name}</option>
                                ))}
                        </optgroup>
                        
                        <optgroup label="Tag Managers">
                            {Object.entries(platformCodes)
                                .filter(([_, p]) => p.category === 'Tag Manager')
                                .map(([key, platform]) => (
                                    <option key={key} value={key}>{platform.name}</option>
                                ))}
                        </optgroup>
                    </select>
                </div>

                {/* Selected Platform Content */}
                {selectedPlatform && (
                <div className="border-t pt-6">
                    <div className="mb-4">
                        <h3 className="font-medium mb-2 flex items-center gap-2">
                            <FontAwesomeIcon icon={platformCodes[selectedPlatform].icon} />
                            {platformCodes[selectedPlatform].name} Integration
                        </h3>
                        {/* Difficulty and Time badges */}
                        <div className="flex gap-3">
                            {platformCodes[selectedPlatform].difficulty && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                    platformCodes[selectedPlatform].difficulty.includes('Easy') ? 'bg-green-100 text-green-700' :
                                    platformCodes[selectedPlatform].difficulty.includes('Medium') ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                }`}>
                                    {platformCodes[selectedPlatform].difficulty}
                                </span>
                            )}
                            {platformCodes[selectedPlatform].timeEstimate && (
                                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                                    ⏱️ {platformCodes[selectedPlatform].timeEstimate}
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-medium mb-2">Code:</h4>
                            <div className="relative">
                                <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
                                    <code>{platformCodes[selectedPlatform].code}</code>
                                </pre>
                                <button
                                    onClick={() => copyToClipboard(platformCodes[selectedPlatform].code)}
                                    className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700"
                                >
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            </div>
                        </div>
                        
                        <div>
                            <h4 className="font-medium mb-2">Instructions:</h4>
                            <div className="space-y-2">
                                {platformCodes[selectedPlatform].instructions.map((instruction, index) => {
                                    // Check if instruction starts with a number (e.g., "1.", "2.") or is a special line
                                    const isNumbered = /^\d+\./.test(instruction);
                                    const isNote = instruction.startsWith('✅') || instruction.startsWith('⚠️') || 
                                                   instruction.startsWith('📌') || instruction.startsWith('💡') || 
                                                   instruction.startsWith('🟢') || instruction.startsWith('📧') ||
                                                   instruction.startsWith('•') || instruction === '';
                                    
                                    if (isNumbered || isNote) {
                                        return (
                                            <div key={index} className="text-sm text-gray-600">
                                                {instruction}
                                            </div>
                                        );
                                    } else {
                                        // For non-numbered items, add them to a list
                                        return (
                                            <div key={index} className="text-sm text-gray-600 pl-4">
                                                • {instruction}
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                )}
            </div>

            {/* Advanced Configuration - Hidden for now */}

            {/* Troubleshooting */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-yellow-500" />
                    Troubleshooting
                </h2>
                
                <div className="space-y-4">
                    <div className="border-l-4 border-yellow-400 pl-4">
                        <h3 className="font-medium">Widget not appearing?</h3>
                        <ul className="text-sm text-gray-600 mt-1 space-y-1">
                            <li>• Check if the script is loading (use browser DevTools)</li>
                            <li>• Verify the client ID is correct</li>
                            <li>• Ensure no JavaScript errors in console</li>
                            <li>• Check for Content Security Policy (CSP) restrictions</li>
                        </ul>
                    </div>
                    
                    <div className="border-l-4 border-yellow-400 pl-4">
                        <h3 className="font-medium">Widget loading slowly?</h3>
                        <ul className="text-sm text-gray-600 mt-1 space-y-1">
                            <li>• Add async attribute to script tag</li>
                            <li>• Consider lazy loading on user interaction</li>
                            <li>• Check your site's overall performance</li>
                        </ul>
                    </div>
                    
                    <div className="border-l-4 border-blue-400 pl-4">
                        <h3 className="font-medium">Need help?</h3>
                        <p className="text-sm text-gray-600 mt-1">Contact support with the following information:</p>
                        <div className="mt-2 space-y-1">
                            <p className="text-sm">
                                <strong>Client ID:</strong> <code className="bg-gray-100 px-2 py-1 rounded">{clientId}</code>
                            </p>
                            <p className="text-sm">
                                <strong>Client:</strong> <span className="text-gray-700">{clientInfo.name}</span>
                            </p>
                            <p className="text-sm">
                                <strong>Website:</strong> <a href={clientInfo.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{clientInfo.website_url}</a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IntegrationsEnhanced;